import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Save, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import io from "socket.io-client";
const base_url = process.env.REACT_APP_API_URL;

const numberData = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const diceFaces = ["⚀", "⚁", "⚂", "⚃", "⚄", "⚅"];

const getDiceDisplay = (num) => diceFaces[num - 1] || "?";

const getSelectedDisplay = (result) => {
  if (!result) {
    return "Select Die";
  }
  return getDiceDisplay(parseInt(result));
};

const AdminManageLucky7 = () => {
  const token = localStorage.getItem("admin_token");
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    !(window.innerWidth < 768),
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentGame, setCurrentGame] = useState(null);
  const [gameBets, setGameBets] = useState([]);
  const [gameHistory, setGameHistory] = useState([]);
  const [totalBets, setTotalBets] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [timmer, setTimmer] = useState(30);
  const [die1, setDie1] = useState("");
  const [die2, setDie2] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOpen1, setIsOpen1] = useState(false);
  const [isOpen2, setIsOpen2] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameBetsDetails, setGameBetsDetails] = useState([]);
  const [currentHistoryPage, setCurrentHistoryPage] = useState(1);
  const [totalHistoryPages, setTotalHistoryPages] = useState(1);
  const socketRef = useRef(null);
  const HISTORY_LIMIT = 10;

  // Socket setup
  useEffect(() => {
    if (!token) return;

    socketRef.current = io(base_url, {
      auth: { token },
      transports: ["websocket", "polling"],
    });

    socketRef.current.on("connect", () => {
      // console.log("Admin socket connected");
      socketRef.current.emit("joinRoom", "admin");
    });

    socketRef.current.on("newGame", () => {
      fetchCurrentGame();
      fetchHistory();
    });

    socketRef.current.on("newBetLucky7", (newBet) => {
      setGameBets((prev) => {
        const updated = [...prev, newBet];

        const totalBetCount = updated.length;
        const totalBetAmount = updated.reduce(
          (sum, bet) => sum + parseFloat(bet.betAmount || 0),
          0,
        );

        setTotalBets(totalBetCount);
        setTotalAmount(totalBetAmount);

        return updated;
      });
    });

    socketRef.current.on("gameUpdated", () => {
      fetchCurrentGame();
      fetchHistory();
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, [token]);

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Fetch current game data
  const fetchCurrentGame = async () => {
    try {
      const response = await axios.get(
        `${base_url}/api/admin/lucky7/current-game`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (response?.data.success) {
        setCurrentGame(response.data.game);
        setGameBets(response.data.bets || []);
        const resultParts = response.data.game?.result?.split(", ") || [];
        setDie1(resultParts[0] || "");
        setDie2(resultParts[1] || "");
        setTimmer(response.data.timmer);

        // Calculate totals
        const totalBetCount = response.data.bets?.length || 0;
        const totalBetAmount =
          response.data.bets?.reduce(
            (sum, bet) => sum + parseFloat(bet.betAmount),
            0,
          ) || 0;
        setTotalBets(totalBetCount);
        setTotalAmount(totalBetAmount);
      } else {
        toast.error(response?.data.message || "Failed to fetch game data");
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  useEffect(() => {
    let interval = null;
    if (timmer > 0) {
      interval = setInterval(() => {
        setTimmer((prev) => prev - 1);
      }, 1000);
    } else if (timmer === 0) {
      clearInterval(interval);
      setTimmer(30);
      handleRefresh();
    }
    return () => clearInterval(interval);
  }, [timmer]);

  // Fetch game history
  const fetchHistory = async () => {
    const offset = (currentHistoryPage - 1) * HISTORY_LIMIT;
    try {
      const response = await axios.get(
        `${base_url}/api/admin/lucky7/game-history?limit=${HISTORY_LIMIT}&offset=${offset}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (response?.data.success) {
        setGameHistory(response.data.history || []);
        setTotalHistoryPages(response.data.totalPages || 1);
      } else {
        toast.error(response?.data.message || "Failed to fetch game history");
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  // Set result
  const handleSetResult = async () => {
    if (!die1 || !die2) {
      toast.error("Please select both dice");
      return;
    }

    setIsProcessing(true);
    try {
      const response = await axios.post(
        `${base_url}/api/admin/lucky7/set-result`,
        {
          game_id: currentGame?.game_id,
          result: `${die1}, ${die2}`,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (response?.data.success) {
        toast.success("Result set and game processed successfully!");
        fetchCurrentGame(); // Refresh current game data
        fetchHistory(); // Refresh history
      } else {
        toast.error(response?.data.message || "Failed to set result");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Error setting result");
    } finally {
      setIsProcessing(false);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    fetchCurrentGame();
    fetchHistory();
  };

  // Handle page change for history
  const handleHistoryPageChange = (newPage) => {
    setCurrentHistoryPage(newPage);
  };

  useEffect(() => {
    fetchCurrentGame();
    fetchHistory();
  }, [currentHistoryPage]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsSidebarOpen(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!currentGame) {
    return (
      <div className="wrapper">
        <div
          className={`content-wrapper d-flex flex-column ${
            !isMobile && isSidebarOpen ? "" : "open-content-wrapper"
          }`}
        >
          <div className="page-content">
            <div className="px-3" style={{ overflowX: "hidden" }}>
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "80vh" }}
              >
                <div className="text-center">
                  <i className="fa fa-spinner fa-spin fa-3x text-primary mb-3"></i>
                  <h4 className="text-center">Loading Lucky7 Game Data...</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleViewGameDetails = async (gameId) => {
    setIsProcessing(true);
    try {
      const response = await axios.get(
        `${base_url}/api/admin/lucky7/game-details?game_id=${gameId}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (response?.data.success) {
        const gameData = gameHistory.find((g) => g.game_id === gameId);
        setSelectedGame({ ...gameData, bets: response.data.bets || [] });
        setGameBetsDetails(response.data.bets || []);
        setShowDetailsModal(true);
      } else {
        toast.error(response?.data.message || "Failed to fetch game details");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Error fetching game details");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div className="wrapper lucky7_admin_manage">
        {/* Content Wrapper */}
        <div
          className={`content-wrapper d-flex flex-column ${
            !isMobile && isSidebarOpen ? "" : "open-content-wrapper"
          }`}
          style={{
            marginLeft: !isMobile && isSidebarOpen ? "" : "0",
            transition: "margin-left 0.3s ease",
          }}
        >
          <div className="page-content">
            <div className="px-2 px-md-3" style={{ overflowX: "hidden" }}>
              {/* Header Section */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-2">
                <div className="text-center text-md-start">
                  <h2 className="mb-1 h4 h3-md">Manage Lucky7 Game</h2>
                  <p className="text-muted mb-0 small">
                    <span className="d-block d-md-inline">
                      Game ID: <strong>{currentGame.game_id}</strong>
                    </span>
                    <span className="d-block d-md-inline ms-md-2">
                      Status:{" "}
                      <span
                        className={`fw-bold ${
                          currentGame.status === 0
                            ? "text-success"
                            : "text-secondary"
                        }`}
                      >
                        {currentGame.status === 0
                          ? "Active (Bets Open)"
                          : "Closed (Result Set)"}
                      </span>
                    </span>
                  </p>
                </div>
                <div className="d-flex gap-2 w-100 w-md-auto justify-content-center justify-content-md-end">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleRefresh}
                  >
                    <RefreshCw size={14} className="me-1" /> Refresh
                  </button>
                  {currentGame.status === 0 && (
                    <button
                      className="btn btn-success btn-sm"
                      onClick={handleSetResult}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <RefreshCw size={14} className="me-1 fa-spin" />{" "}
                          Processing...
                        </>
                      ) : (
                        <>
                          <Save size={14} className="me-1" /> Set Result
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Stats Cards */}
              <div className="row mb-4 g-3 g-md-4">
                <div className="col-6 col-md-3">
                  <div className="card rounded-3 rounded-md-4 shadow-sm border-start border-4 border-primary h-100">
                    <div className="card-body text-center p-2 p-md-3">
                      <i className="fa fa-rupee-sign fa-lg fa-2x-md text-primary mb-2"></i>
                      <h4 className="text-primary fw-bold h5 h4-md">
                        ₹{totalAmount.toFixed(2)}
                      </h4>
                      <p className="text-muted mb-0 small">Total Bet Amount</p>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card rounded-3 rounded-md-4 shadow-sm border-start border-4 border-success h-100">
                    <div className="card-body text-center p-2 p-md-3">
                      <i className="fa fa-ticket fa-lg fa-2x-md text-success mb-2"></i>
                      <h4 className="text-success fw-bold h5 h4-md">
                        {totalBets}
                      </h4>
                      <p className="text-muted mb-0 small">Total Bets Placed</p>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card rounded-3 rounded-md-4 shadow-sm border-start border-4 border-info h-100">
                    <div className="card-body text-center p-2 p-md-3">
                      <i className="fa fa-clock fa-lg fa-2x-md text-info mb-2"></i>
                      <h4 className="text-info fw-bold h5 h4-md">{timmer}s</h4>
                      <p className="text-muted mb-0 small">Round Timer</p>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card rounded-3 rounded-md-4 shadow-sm border-start border-4 border-warning h-100">
                    <div className="card-body text-center p-2 p-md-3">
                      <i className="fa fa-dice fa-lg fa-2x-md text-warning mb-2"></i>
                      <h4 className="text-warning fw-bold h5 h4-md">
                        {numberData.length}
                      </h4>
                      <p className="text-muted mb-0 small">Sums</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bets Table */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card rounded-3 rounded-md-4 shadow-sm">
                    <div className="card-header bg-light">
                      <h5 className="mb-0">
                        <i className="fa fa-table me-2"></i>Bet Details by Sum
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row mb-4">
                        {[
                          {
                            key: "ANDAR",
                            label: "ANDAR (2–6)",
                            color: "success",
                          },
                          { key: "LUCKY7", label: "LUCKY 7", color: "warning" },
                          {
                            key: "BAHAR",
                            label: "BAHAR (8–12)",
                            color: "primary",
                          },
                        ].map((item) => {
                          const bets = gameBets.filter(
                            (b) => b.bet === item.key,
                          );
                          const totalAmount = bets.reduce(
                            (sum, b) => sum + parseFloat(b.betAmount || 0),
                            0,
                          );

                          return (
                            <div className="col-4" key={item.key}>
                              <div
                                className={`card border-start border-4 border-${item.color} shadow-sm h-100`}
                              >
                                <div className="card-body text-center">
                                  <h5 className={`text-${item.color} fw-bold`}>
                                    {item.label}
                                  </h5>
                                  <h3 className="fw-bold">
                                    ₹{totalAmount.toFixed(2)}
                                  </h3>
                                  <p className="text-muted mb-0">
                                    Bets: {bets.length}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Set Result Section (if game is active) */}
              {currentGame.status === 0 && (
                <div className="row">
                  <div className="col-12">
                    <div
                      className="card rounded-3 rounded-md-4 shadow-sm"
                      style={{ overflow: "unset" }}
                    >
                      <div className="card-header bg-light d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                        <h5 className="mb-0">
                          <i className="fa fa-edit me-2"></i>Set Game Result
                        </h5>
                        <small className="text-muted text-center text-md-end">
                          Select two dice (1-6) for the result
                        </small>
                      </div>
                      <div className="card-body text-center lucky7-admin-dice">
                        <h4 className="mb-4 fw-bold text-primary">
                          🎲 Click Dice to Set Result
                        </h4>

                        {/* DIE 1 */}
                        <h6 className="mb-2 text-muted">Die 1</h6>
                        <div className="d-flex justify-content-center gap-3 mb-4 flex-wrap">
                          {[1, 2, 3, 4, 5, 6].map((val) => (
                            <button
                              key={`die1-${val}`}
                              className={`dice-btn ${
                                die1 === val ? "dice-selected" : ""
                              }`}
                              onClick={() => setDie1(val)}
                            >
                              {getDiceDisplay(val)}
                            </button>
                          ))}
                        </div>

                        {/* DIE 2 */}
                        <h6 className="mb-2 text-muted">Die 2</h6>
                        <div className="d-flex justify-content-center gap-3 mb-4 flex-wrap">
                          {[1, 2, 3, 4, 5, 6].map((val) => (
                            <button
                              key={`die2-${val}`}
                              className={`dice-btn ${
                                die2 === val ? "dice-selected" : ""
                              }`}
                              onClick={() => setDie2(val)}
                            >
                              {getDiceDisplay(val)}
                            </button>
                          ))}
                        </div>

                        {/* ACTION */}
                        <button
                          className="btn btn-lg btn-success px-5 fw-bold"
                          onClick={handleSetResult}
                          disabled={isProcessing || !die1 || !die2}
                        >
                          {isProcessing
                            ? "🎰 Processing..."
                            : "🎯 Set & Process Result"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Game History Table */}
              <div className="row">
                <div className="col-12">
                  <div className="card rounded-3 rounded-md-4 shadow-sm">
                    <div className="card-header bg-light">
                      <h5 className="mb-0">
                        <i className="fa fa-history me-2"></i>Recent Game
                        History
                      </h5>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-dark">
                            <tr>
                              <th scope="col" className="col-2 col-md-1">
                                Game ID
                              </th>
                              <th scope="col" className="col-3 col-md-3">
                                Result
                              </th>
                              <th scope="col" className="col-2 col-md-2">
                                Total Bets
                              </th>
                              <th
                                scope="col"
                                className="col-2 col-md-2 text-end"
                              >
                                Total Amount (₹)
                              </th>
                              <th scope="col" className="col-2 col-md-2">
                                Status
                              </th>
                              <th scope="col" className="col-1 col-md-2">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {gameHistory.map((game, idx) => {
                              const dice = game.result
                                ? game.result
                                    .split(", ")
                                    .map((n) =>
                                      getDiceDisplay(parseInt(n.trim())),
                                    )
                                : [null, null];
                              const sum = game.result
                                ? parseInt(game.result.split(", ")[0]) +
                                  parseInt(game.result.split(", ")[1])
                                : null;
                              return (
                                <tr key={idx}>
                                  <td className="col-2 col-md-1">
                                    {game.game_id}
                                  </td>
                                  <td className="col-3 col-md-3">
                                    <span className="me-1 me-md-2 fs-5">
                                      {dice[0]}
                                    </span>
                                    <span className="me-1 me-md-2 fs-5">+</span>
                                    <span className="me-1 me-md-2 fs-5">
                                      {dice[1]}
                                    </span>
                                    <span>= {sum}</span>
                                  </td>
                                  <td className="col-2 col-md-2">
                                    {game.total_bets || 0}
                                  </td>
                                  <td className="col-2 col-md-2 text-end">
                                    ₹{game.total_amount?.toFixed(2) || 0}
                                  </td>
                                  <td className="col-2 col-md-2">
                                    <span className="badge bg-success small">
                                      Completed
                                    </span>
                                  </td>
                                  <td className="col-1 col-md-2 text-center">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        handleViewGameDetails(game.game_id)
                                      }
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            {gameHistory.length === 0 && (
                              <tr>
                                <td
                                  colSpan="6"
                                  className="text-center text-muted py-4"
                                >
                                  No history available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      {/* Pagination */}
                      {totalHistoryPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-top">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                              handleHistoryPageChange(
                                Math.max(currentHistoryPage - 1, 1),
                              )
                            }
                            disabled={currentHistoryPage === 1}
                          >
                            <ChevronLeft size={16} className="me-1" />
                            Previous
                          </button>
                          <span className="small text-muted">
                            Page {currentHistoryPage} of {totalHistoryPages}
                          </span>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                              handleHistoryPageChange(
                                Math.min(
                                  currentHistoryPage + 1,
                                  totalHistoryPages,
                                ),
                              )
                            }
                            disabled={currentHistoryPage === totalHistoryPages}
                          >
                            Next
                            <ChevronRight size={16} className="ms-1" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Game Details Modal */}
              {showDetailsModal && selectedGame && (
                <div
                  className="modal fade show d-block"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                  tabIndex="-1"
                >
                  <div className="modal-dialog modal-xl">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          Game Details - ID: {selectedGame.game_id}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowDetailsModal(false)}
                        ></button>
                      </div>
                      <div className="modal-body">
                        <div className="row mb-3">
                          <div className="col-md-3">
                            <strong>Result:</strong>
                            <div>
                              {selectedGame.result
                                ? `${getDiceDisplay(
                                    parseInt(
                                      selectedGame.result.split(", ")[0],
                                    ),
                                  )} + ${getDiceDisplay(
                                    parseInt(
                                      selectedGame.result.split(", ")[1],
                                    ),
                                  )} = ${
                                    parseInt(
                                      selectedGame.result.split(", ")[0],
                                    ) +
                                    parseInt(selectedGame.result.split(", ")[1])
                                  }`
                                : "No result"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <strong>Total Bets:</strong>{" "}
                            {selectedGame.total_bets || 0}
                          </div>
                          <div className="col-md-3">
                            <strong>Total Amount:</strong> ₹
                            {selectedGame.total_amount?.toFixed(2) || 0}
                          </div>
                          <div className="col-md-3">
                            <strong>Status:</strong>{" "}
                            <span className="badge bg-success">Completed</span>
                          </div>
                        </div>
                        <h6 className="mb-2">Bet Details:</h6>
                        <div className="table-responsive">
                          <table className="table table-hover mb-0">
                            <thead className="table-dark">
                              <tr>
                                <th>Phone</th>
                                <th>Bet Sum</th>
                                <th>Amount (₹)</th>
                                <th>Status</th>
                                <th>Win Amount (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {gameBetsDetails.map((bet, idx) => (
                                <tr key={idx}>
                                  <td>{bet.phone}</td>
                                  <td>{bet.bet}</td>
                                  <td>
                                    ₹{parseFloat(bet.betAmount).toFixed(2)}
                                  </td>
                                  <td>
                                    <span
                                      className={`badge ${
                                        bet.status === 1
                                          ? "bg-success"
                                          : bet.status === 2
                                            ? "bg-danger"
                                            : "bg-warning"
                                      }`}
                                    >
                                      {bet.status === 1
                                        ? "Win"
                                        : bet.status === 2
                                          ? "Lose"
                                          : "Pending"}
                                    </span>
                                  </td>
                                  <td>
                                    {bet.winAmount
                                      ? `+₹${parseFloat(bet.winAmount).toFixed(
                                          2,
                                        )}`
                                      : "-"}
                                  </td>
                                </tr>
                              ))}
                              {gameBetsDetails.length === 0 && (
                                <tr>
                                  <td
                                    colSpan="5"
                                    className="text-center text-muted py-3"
                                  >
                                    No bets for this game
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowDetailsModal(false)}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 767.98px) {
          .h3-md {
            font-size: 1.75rem !important;
          }
          .fa-2x-md {
            font-size: 1.5rem !important;
          }
          .scrollable-dropdown {
            max-height: 200px;
            overflow-y: auto;
          }
        }
        @media (min-width: 768px) {
          .h4-md {
            font-size: 1.5rem !important;
          }
          .fa-2x-md {
            font-size: 2rem !important;
          }
        }
        /* ===== ADMIN DICE UI ===== */
        .lucky7-admin-dice {
          background: radial-gradient(circle at top, #fffbe6, #f5e6a8);
          padding: 25px;
        }

        /* Dice Button */
        .dice-btn {
          width: 80px;
          height: 80px;
          font-size: 42px;
          border-radius: 14px;
          border: 3px solid #ccc;
          background: linear-gradient(145deg, #ffffff, #eaeaea);
          box-shadow: 0 6px 14px rgba(0, 0, 0, 0.25);
          transition: all 0.2s ease;
          cursor: pointer;
        }

        /* Hover */
        .dice-btn:hover {
          transform: scale(1.08);
          border-color: #ff9800;
        }

        /* Selected Dice */
        .dice-selected {
          background: linear-gradient(145deg, #4caf50, #2e7d32);
          color: #fff;
          border-color: #1b5e20;
          box-shadow: 0 0 20px rgba(76, 175, 80, 0.9);
          transform: scale(1.15);
        }

        /* Disabled action button */
        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .dice-btn {
            width: 65px;
            height: 65px;
            font-size: 34px;
          }
        }
          
      `}
      </style>
    </>
  );
};

export default AdminManageLucky7;
